/**
 * useFormField Hook
 *
 * A wrapper around Formik's useField that provides a library-agnostic API.
 * Returns field state, metadata, and helper functions.
 *
 * @example
 * ```typescript
 * import { useFormField, FieldError } from '@/forms/core';
 *
 * const MyInput = ({ name }) => {
 *   const [field] = useFormField(name);
 *
 *   return (
 *     <div>
 *       <input {...field} />
 *       <FieldError name={name} />
 *     </div>
 *   );
 * };
 * ```
 */

import { useField as useFormikField } from 'formik';
import type { FieldState, FieldMeta, FieldHelpers } from '../../types';

/**
 * Input type for useFormField - accepts string or object with name property
 */
type FieldNameOrProps = string | { name: string; [key: string]: unknown };

/**
 * Extract name from string or props object
 */
function getFieldName(nameOrProps: FieldNameOrProps): string {
  return typeof nameOrProps === 'string' ? nameOrProps : nameOrProps.name;
}

/**
 * Hook to access field state, metadata, and helpers
 *
 * @param nameOrProps - The field name/path or props object with name property
 * @returns Tuple of [fieldState, fieldMeta, fieldHelpers]
 */
export function useFormField<T = unknown>(
  nameOrProps: FieldNameOrProps,
): [FieldState<T>, FieldMeta, FieldHelpers<T>] {
  const name = getFieldName(nameOrProps);
  const [field, meta, helpers] = useFormikField<T>(name);

  const fieldState: FieldState<T> = {
    name: field.name,
    value: field.value,
    onChange: field.onChange,
    onBlur: field.onBlur,
  };

  const fieldMeta: FieldMeta = {
    touched: meta.touched,
    error: meta.error,
    initialValue: meta.initialValue,
  };

  const fieldHelpers: FieldHelpers<T> = {
    setValue: helpers.setValue,
    setTouched: helpers.setTouched,
    setError: helpers.setError,
  };

  return [fieldState, fieldMeta, fieldHelpers];
}

/**
 * Hook to get field input props (spread-friendly)
 * Combines field state with error information
 *
 * @example
 * ```typescript
 * const inputProps = useFieldInput('email');
 * return <input {...inputProps} />;
 * ```
 */
export function useFieldInput<T = unknown>(nameOrProps: FieldNameOrProps) {
  const [field, meta] = useFormField<T>(nameOrProps);

  return {
    ...field,
    hasError: meta.touched && !!meta.error,
    errorMessage: meta.touched ? meta.error : undefined,
  };
}
